import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Carousel from 'common/bstCarousel';
import { AppConfig } from 'AppConfig';
import { useSelector } from 'react-redux';
import * as AppActionTypes from 'features/frontoffice/actions/actions';
import { store } from 'store/store';
import * as UserTypes from 'features/user/userTypes';
import { Box, Grid, MenuList } from '@mui/material';
import SideNav from './sideNav';
import makeStyles from '@mui/styles/makeStyles';
import SideNavItem from './sideNavItem';
import NoAvailableReports from 'features/frontoffice/component/noAvailableReports';
import { sortReports } from 'utils/reportSort';

const allText = 'All';

const SAVED_NEWEST_KEYS = ['updateDt', 'createdDt', 'createDt'];

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex'
  },
  header: {
    justifyContent: 'center',
    textAlign: 'start',
    color: '#4051B5',
    marginBottom: 15
  },
  menu: {
    maxWidth: 200,
    textAlign: 'start',
    minWidth: 180
  }
}));

const SavedReports = ({ searchTerm = '', sortBy }) => {
  const classes = useStyles();
  const state = {
    screenWidth: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  };

  const sortKey = typeof sortBy === 'string' ? sortBy : sortBy?.value;

  const adHocPublic = useSelector((state) => state.fo.adHocPublic);
  const filteredAdHocPersonal = useSelector((state) => state.fo.filteredAdHocPersonal);
  const filteredAdHocPublic = useSelector((state) => state.fo.filteredAdHocPublic);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);

  const carouselStyle = { zIndex: 1000 };
  const [data, setData] = useState(filteredAdHocPublic);
  const [folderKey, setFolderKey] = useState(allText);
  // const isLightTouch = useFeatureFlag('light-touch');

  const buildFolders = (arr = []) => {
    let myMap = new Map();
    for (let i = 0; i < arr.length; i += 1) {
      let node = arr[i];
      if (node?.reportTag) {
        if (myMap.has(node?.reportTag)) {
          myMap.get(node?.reportTag).push(node);
        } else {
          myMap.set(node?.reportTag, new Array(node));
        }
      }
    }
    return new Map([...myMap.entries()].sort());
  };

  const [folders, updateFolders] = useState(buildFolders(filteredAdHocPublic));

  useEffect(() => {
    store.dispatch({
      type: UserTypes.APP_SWITCHED,
      payload: AppConfig.HEADER_MENU_TYPE.STANDARD
    });
  }, []);

  useEffect(() => {
    store.dispatch(AppActionTypes.getSaved());
  }, [currentTenantCd]);

  useEffect(() => {
    if (filteredAdHocPublic) {
      updateFolders(buildFolders(filteredAdHocPublic));
      setData(filteredAdHocPublic);
    }
  }, [filteredAdHocPublic]);

  useEffect(() => {
    if (folders?.size === 1) {
      setFolderKey(allText);
    }
  }, [folders]);

  useEffect(() => {
    setData(adHocPublic);
  }, [adHocPublic]);

  const normalizedSearch = (searchTerm || '').trim().toLowerCase();

  const applySearchFilter = useCallback(
    (items = []) => {
      const list = Array.isArray(items) ? items : [];
      if (!normalizedSearch) return list;
      return list.filter((item) => {
        const title = (item?.displayText || '').toLowerCase();
        const desc = (item?.description || '').toLowerCase();
        return title.includes(normalizedSearch) || desc.includes(normalizedSearch);
      });
    },
    [normalizedSearch]
  );

  const filteredPersonal = useMemo(() => applySearchFilter(filteredAdHocPersonal), [filteredAdHocPersonal, applySearchFilter]);
  const sortedPersonal = useMemo(
    () => sortReports(filteredPersonal, sortKey, { newestKeys: SAVED_NEWEST_KEYS }),
    [filteredPersonal, sortKey]
  );
  const publicBaseItems = useMemo(() => (folders.size === 1 || folderKey === allText ? data : folders.get(folderKey)), [folders, folderKey, data]);
  const filteredPublic = useMemo(() => applySearchFilter(publicBaseItems), [publicBaseItems, applySearchFilter]);
  const sortedPublic = useMemo(
    () => sortReports(filteredPublic, sortKey, { newestKeys: SAVED_NEWEST_KEYS }),
    [filteredPublic, sortKey]
  );

  return (
    <>
      {Array.isArray(sortedPersonal) && sortedPersonal.length > 0 && (
        <>
          <div className="carousel-title" style={{ fontFamily: 'Rubik', marginTop: 0 }}>
            Ad Hoc Personal
          </div>

          <div className="has-carousel frequently-run-carousel">
            <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end"></Box>
            <Grid container>
              {folders?.size > 1 && (
                <Grid item xs={2}>
                  <MenuList open className={classes.menu} sx={{ mb: '0.5rem' }}>
                    <SideNavItem key={'side-nav-personal'} item={[{ reportTag: 'Personal' }]} filter={() => {}} total={filteredAdHocPersonal.length ?? ''} isPersonal />
                  </MenuList>
                </Grid>
              )}
              <Grid item xs={12} style={carouselStyle}>
                {/* {folders?.size > 1 && !isLightTouch && (
                  <Typography className={classes.header} variant="h6">
                    {'Personal'}
                  </Typography>
                )} */}
                <Carousel
                  type={AppConfig.AD_HOC_CAROUSEL}
                  items={sortedPersonal}
                  viewWidth={state.screenWidth}
                  cardWidth={AppConfig.HOME_CUSTOM_CARD_WIDTH}
                  cardHeight={AppConfig.HOME_CUSTOM_CARD_HEIGHT}
                />
              </Grid>
            </Grid>
          </div>
        </>
      )}
      {Array.isArray(sortedPublic) && !!sortedPublic?.length && (
        <>
          <div className="carousel-title" style={{ fontFamily: 'Rubik' }}>
            Ad Hoc Public
          </div>
          <div className="has-carousel frequently-run-carousel">
            <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end"></Box>
            <Grid container>
              {folders?.size > 1 && (
                <Grid item xs={2}>
                  <SideNav
                    key={'saved-report-nav-' + folderKey}
                      allLength={Array.isArray(sortedPublic) ? sortedPublic.length : 0}
                    folders={folders}
                    setData={setData}
                    setFolderKey={setFolderKey}
                    folderKey={folderKey}
                  />
                </Grid>
              )}

              <Grid item xs={12} style={carouselStyle}>
                {folders?.size > 0 && (
                  <>
                    {/* {folders?.size > 1 && !isLightTouch && (
                      <Typography className={classes.header} variant="h6">
                        {folderKey}
                      </Typography>
                    )} */}
                    <Carousel
                      key={'saved-report-carousel-' + folderKey}
                      type={AppConfig.AD_HOC_CAROUSEL}
                      items={sortedPublic}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </div>
        </>
      )}
      {!filteredPersonal.length && !filteredPublic.length && (
        <NoAvailableReports />
      )}
    </>
  );
};

export default SavedReports;
